<template>
  <div class="px-6 lg:px-10 py-6">
    <h1 class="text-[26px] font-bold text-gray-900 mb-12">Tax Report</h1>

    <div v-if="!canView" class="mt-10"></div>

    <DynamicReportForm
      v-else
      v-model="model"
      :fields="fields"
      :columns="2"
      stackAt="md"
      contentWidth="2xl"
      actionLabel="View Report"
      :showTitle="false"
      :loading="loading"
      :errors="errors"
      @submit="onSubmit"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue"
import DynamicReportForm from "@/components/DynamicReportForm.vue"
import { loadMyPermissions, hasAny } from "@/utils/permissions"
import api from "@/api"
import Swal from "sweetalert2"

const canView = ref(false)
const loading = ref(false)
const errors  = ref({})

onMounted(async () => {
  await loadMyPermissions(false)
  canView.value = hasAny(["tax_reports.view"])
})

const model = ref({
  company_id: "",
  type:       "",
  from_date:  "",
  to_date:    "",
})

watch(
  model,
  (v) => {
    if (v.from_date)  delete errors.value.from_date
    if (v.to_date)    delete errors.value.to_date
    if (v.type)       delete errors.value.type
  },
  { deep: true }
)

const fields = [
  {
    name:        "company_id",
    label:       "Company",
    type:        "select",
    span:        "full",
    placeholder: "Select company",
    loader: async () => {
      const res = await api.get("/accounting/companies")
      return [
        { label: "All", value: "" },
        ...res.data.map(c => ({ label: c.name, value: c.id })),
      ]
    },
  },
  {
    name:        "type",
    label:       "Type",
    type:        "select",
    span:        "full",
    placeholder: "Select type",
    loader: async () => [
      { label: "Purchases", value: "purchases" },
      { label: "Sales", value: "sales" },
    ],
  },
  { name: "from_date", label: "From", type: "date", span: 1, placeholder: "yyyy/mm/dd" },
  { name: "to_date",   label: "To",   type: "date", span: 1, placeholder: "yyyy/mm/dd" },
]

function validate() {
  const e = {}
  if (!model.value.from_date) e.from_date = "From date is required"
  if (!model.value.to_date)   e.to_date   = "To date is required"
  if (!model.value.type)      e.type      = "Type is required"
  errors.value = e
  return Object.keys(e).length === 0
}

async function onSubmit(_) {
  if (!canView.value || !validate()) return

  const params = new URLSearchParams()
  if (model.value.company_id) params.append("company_id", model.value.company_id)
  if (model.value.from_date)  params.append("from_date",  model.value.from_date)
  if (model.value.to_date)    params.append("to_date",    model.value.to_date)
  if (model.value.type)       params.append("type",       model.value.type)

  loading.value = true
  try {
    const res = await api.get(`/accounting/reports/tax/pdf?${params.toString()}`, {
      responseType: "blob",
    })
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))
    window.open(url, "_blank")
  } catch (err) {
    console.error("Failed to generate report:", err)
    Swal.fire({ icon: "error", title: "Failed to generate report", toast: true, position: "top", timer: 2000, showConfirmButton: false })
  } finally {
    loading.value = false
  }
}
</script>
